<template>
  <div :class="[isModal ? 'map-wrapper-modal' : 'map-wrapper']">
    <div ref="mapRootRef" class="map-view-all" />
    <MapTypeSelector v-model="mapModalStore.mapType" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from '@/assets/images/icon/icon_location_marker_edit.svg'
import { DEFAULT_MAP_POSITION } from '@/constants'
import { roundLatLon7 } from '@/utils/common'
import { useMapModalStore } from '@/stores/mapModal'
import MapTypeSelector from '@/components/map/MapTypeSelector.vue'

/* =========================
 * types
 ========================= */
type RoutePoint = {
  lat: number
  lon: number
  label?: string
  routeWind?: boolean
  upperWind?: boolean
}

type MapType = 'pale' | 'blank' | 'satellite' | 'hillshade'

/* =========================
 * props / emits
 ========================= */
const props = withDefaults(
  defineProps<{
    mode: 'single' | 'route'
    lat?: number | null
    lon?: number | null
    points?: RoutePoint[]
    mapType: 'pale' | 'blank' | 'satellite' | 'hillshade'
    isModal?: boolean
    markerContext?: 'flight-area' | 'route-point'
    markerLabel?: string
    singleMarker?: {
      routeWind?: boolean
      upperWind?: boolean
    }
  }>(),
  {
    points: () => [],
    isModal: false,
    markerContext: 'flight-area',
    singleMarker: () => ({}),
  },
)

const emit = defineEmits<{
  (
    e: 'click:route-point',
    payload: {
      index: number
      lat: number
      lon: number
      routeWind?: boolean
      upperWind?: boolean
      label: string
    },
  ): void

  (
    e: 'click:flight-area',
    payload: {
      lat: number
      lon: number
    },
  ): void

  (e: 'update:lat', value: number): void
  (e: 'update:lon', value: number): void
}>()

/* =========================
 * state
 ========================= */
const mapModalStore = useMapModalStore()
const mapRootRef = ref<HTMLDivElement | null>(null)

let map: L.Map | null = null
let singleMarkerInstance: L.Marker | null = null
let routeMarkers: L.Layer[] = []
let routeLine: L.Polyline | null = null
let currentBaseLayer: L.TileLayer | null = null

const mapInitialized = ref(false)

/* =========================
 * map config
 ========================= */
const DEFAULT_ZOOM = 5
const MIN_ZOOM = 2
const MAX_ZOOM = 30
const SINGLE_PIN_ZOOM = 14

const mapTypeKeyMap: Record<MapType, string> = {
  pale: '淡色地図',
  blank: '白地図',
  satellite: '衛星写真',
  hillshade: '起伏図',
}

const attribution =
  '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank">出典：国土地理院</a>'

const baseLayers: Record<string, L.TileLayer> = {
  淡色地図: L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png', {
    attribution,
    minNativeZoom: 5,
    maxNativeZoom: 18,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
  }),
  白地図: L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/blank/{z}/{x}/{y}.png', {
    attribution,
    minNativeZoom: 5,
    maxNativeZoom: 14,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
  }),
  衛星写真: L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg', {
    attribution,
    minNativeZoom: 2,
    maxNativeZoom: 18,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
  }),
  起伏図: L.tileLayer('https://cyberjapandata.gsi.go.jp/xyz/hillshademap/{z}/{x}/{y}.png', {
    attribution,
    minNativeZoom: 2,
    maxNativeZoom: 16,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
  }),
}

/* =========================
 * helpers
 ========================= */
function clearSingleMarker() {
  if (singleMarkerInstance && map) {
    map.removeLayer(singleMarkerInstance)
    singleMarkerInstance = null
  }
}

function clearRoute() {
  routeMarkers.forEach((m) => map?.removeLayer(m))
  routeMarkers = []

  if (routeLine) {
    map?.removeLayer(routeLine)
    routeLine = null
  }
}

/* =========================
 * single pin
 ========================= */
function drawSingleMarker() {
  if (!map || props.lat == null || props.lon == null) return

  clearSingleMarker()

  singleMarkerInstance = L.marker([props.lat, props.lon], {
    icon: L.divIcon({
      className: 'leaflet-div-icon',
      html: getSingleMarkerHtml(),
      iconSize: props.markerContext === 'route-point' ? [42, 42] : [40, 40],
      iconAnchor: props.markerContext === 'route-point' ? [21, 21] : [20, 40],
    }),
    draggable: props.isModal,
  }).addTo(map)

  singleMarkerInstance.on('click', () => {
    emit('click:flight-area', {
      lat: props.lat!,
      lon: props.lon!,
    })
  })

  if (props.isModal) {
    singleMarkerInstance.on('dragend', (e) => {
      const pos = (e.target as L.Marker).getLatLng()
      emit('update:lat', roundLatLon7(pos.lat))
      emit('update:lon', roundLatLon7(pos.lng))
    })
  }
}

/* =========================
 * route
 ========================= */
function drawRoute(points: RoutePoint[]) {
  if (!map) return
  clearRoute()

  const latlngs: L.LatLngExpression[] = []

  points.forEach((p, index) => {
    const marker = L.marker([p.lat, p.lon], {
      icon: L.divIcon({
        className: 'leaflet-div-icon route-marker',
        html: getRouteMarkerHtml({
          label: p.label ?? String(index + 1),
          routeWind: p.routeWind,
          upperWind: p.upperWind,
        }),
        iconSize: [42, 42],
        iconAnchor: [21, 21],
      }),
      interactive: true,
    }).addTo(map!)

    marker.on('click', () => {
      emit('click:route-point', {
        index,
        lat: p.lat,
        lon: p.lon,
        routeWind: p.routeWind,
        upperWind: p.upperWind,
        label: p.label ?? String(index + 1),
      })
    })

    routeMarkers.push(marker)
    latlngs.push([p.lat, p.lon])
  })

  if (latlngs.length >= 2) {
    routeLine = L.polyline(latlngs, {
      color: '#222',
      weight: 3,
    }).addTo(map!)
  }
}

function getRouteMarkerHtml({
  label,
  routeWind,
  upperWind,
}: {
  label: string
  routeWind?: boolean
  upperWind?: boolean
}) {
  // 両方ON
  if (routeWind && upperWind) {
    return `
      <div class="maker-bese marker-all"><span class="center">${label}</span></div>
    `
  }

  // 上空の風のみ
  if (upperWind) {
    return `<div class="maker-bese marker-square">${label}</div>`
  }

  // 航路上の風のみ
  if (routeWind) {
    return `<div class="maker-bese marker-circle">${label}</div>`
  }

  // 両方OFF
  return `<div class="maker-bese marker-default">${label}</div>`
}

function reloadMap(lat?: number | null, lon?: number | null) {
  if (!map) return
  // まず必ずピンを消す
  clearSingleMarker()

  // エリア未設定 → 既定位置へ（ピンなし）
  if (lat == null || lon == null) {
    map.invalidateSize(true)
    map.setView([DEFAULT_MAP_POSITION.lat, DEFAULT_MAP_POSITION.lon], DEFAULT_ZOOM, {
      animate: false,
    })
    return
  }

  // エリア設定済
  map.invalidateSize(true)
  map.setView([lat, lon], map.getZoom(), { animate: false })
  drawSingleMarker()
}

function getPinPosition() {
  if (!singleMarkerInstance) return null
  const pos = singleMarkerInstance.getLatLng()
  return {
    lat: roundLatLon7(pos.lat),
    lon: roundLatLon7(pos.lng),
  }
}

function getSingleMarkerHtml() {
  if (props.markerContext === 'flight-area') {
    return `
      <img
        src="${markerIcon}"
        width="40"
        height="40"
      />
    `
  }

  // ===== route-point（親マップと同じデザイン） =====
  const label = props.markerLabel ?? ''
  const routeWind = props.singleMarker?.routeWind
  const upperWind = props.singleMarker?.upperWind

  // 両方ON
  if (routeWind && upperWind) {
    return `
      <div class="maker-bese marker-all"><span class="center">${label}</span></div>
    `
  }

  // 上空の風のみ
  if (upperWind) {
    return `<div class="maker-bese marker-square">${label}</div>`
  }

  // 航路上の風のみ
  if (routeWind) {
    return `<div class="maker-bese marker-circle">${label}</div>`
  }

  // 両方OFF
  return `<div class="maker-bese marker-default">${label}</div>`
}

function fitToPoints(map: L.Map, points: { lat: number; lon: number }[], isModal = false) {
  if (points.length === 0) return

  // 1点のみ
  if (points.length === 1) {
    const p0 = points[0]
    if (!p0) return
    map.setView(
      [p0.lat, p0.lon],
      isModal ? 14 : 13, // ← モーダルは少し寄せる
    )
    return
  }

  const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon] as [number, number]))

  map.fitBounds(bounds, {
    padding: isModal ? [20, 20] : [40, 40],
    maxZoom: MAX_ZOOM,
  })
}

/* =========================
 * lifecycle
 ========================= */
onMounted(() => {
  if (!mapRootRef.value || mapInitialized.value) return

  map = L.map(mapRootRef.value, {
    center: [DEFAULT_MAP_POSITION.lat, DEFAULT_MAP_POSITION.lon],
    zoom: DEFAULT_ZOOM,
    zoomControl: true,
    attributionControl: true,
  })

  const mapTypeLabel = mapTypeKeyMap[props.mapType]
  if (mapTypeLabel && baseLayers[mapTypeLabel]) {
    baseLayers[mapTypeLabel].addTo(map)
  }

  L.control
    .layers(baseLayers, undefined, {
      position: 'topright',
    })
    .addTo(map)

  map.on('baselayerchange', (e: L.LayersControlEvent) => {
    const selectedName = e.name

    const nextMapType = (Object.entries(mapTypeKeyMap).find(
      ([, label]) => label === selectedName,
    )?.[0] ?? null) as MapType | null

    if (nextMapType) {
      mapModalStore.setMapType(nextMapType)
    }
  })

  mapInitialized.value = true

  if (props.mode === 'single' && props.lat != null && props.lon != null) {
    map.setView([props.lat!, props.lon!], SINGLE_PIN_ZOOM)
    drawSingleMarker()
  }
  if (props.mode === 'route') {
    drawRoute(props.points ?? [])
    fitToPoints(map, props.points ?? [])
  }

  if (props.isModal) {
    requestAnimationFrame(() => {
      map!.invalidateSize(true)
      fitToPoints(map!, props.points ?? [], true)
    })
  }

  if (mapTypeLabel && baseLayers[mapTypeLabel]) {
    baseLayers[mapTypeLabel].addTo(map)
    currentBaseLayer = baseLayers[mapTypeLabel]
  }
})

watch(
  () => [props.lat, props.lon],
  ([lat, lon]) => {
    if (!map || props.mode !== 'single') return
    if (lat == null || lon == null) return

    requestAnimationFrame(() => {
      map!.invalidateSize(true)
      if (lat == null || lon == null) return
      map!.setView([lat, lon], SINGLE_PIN_ZOOM, { animate: false })
      drawSingleMarker()
    })
  },
)

watch(
  () => props.mode,
  (mode) => {
    if (!map) return
    if (mode === 'single') {
      clearRoute()
      drawSingleMarker()
    } else {
      clearSingleMarker()
      drawRoute(props.points ?? [])
      fitToPoints(map, props.points ?? [])
    }
  },
)

watch(
  () => props.points,
  (points) => {
    if (!map || props.mode !== 'route') return
    drawRoute(points ?? [])
    fitToPoints(map, points ?? [])
  },
  { deep: true },
)

watch(
  () => props.mapType,
  (type) => {
    if (!map) return

    const label = mapTypeKeyMap[type]
    const nextLayer = label !== undefined ? baseLayers[label] : undefined
    if (!nextLayer) return

    // ★ 既存レイヤーを除去
    if (currentBaseLayer) {
      map.removeLayer(currentBaseLayer)
    }

    // ★ 新レイヤーを追加
    nextLayer.addTo(map)
    currentBaseLayer = nextLayer
  },
  { immediate: true },
)

function recenter(lat: number, lon: number) {
  if (!map) return

  requestAnimationFrame(() => {
    map!.invalidateSize(true)
    if (lat == null || lon == null) return
    map!.setView([lat, lon], SINGLE_PIN_ZOOM, { animate: false })
    drawSingleMarker()
  })
}

defineExpose({
  reloadMap,
  getPinPosition,
  recenter,
})
</script>

<style>
.map-wrapper-modal {
  width: 100%;
  aspect-ratio: 800 / 351.13;
  position: relative;
}
@media (max-width: 767px) {
  .map-wrapper-modal {
    width: 100%;
    aspect-ratio: 319 / 153.56;
    position: relative;
  }
}

/* 外枠を消すため */
.leaflet-div-icon {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
</style>
